// Sección Detalle de Ventas - tabla contable con filtros
// Usada en Resumen de ventas (Sales)

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Spinner, Form, Pagination } from 'react-bootstrap';
import { getSaleReportAccounting } from '../services/saleApi';
import { getCategories } from '../services/categoryApi';
import { dotoniwoofApi } from '../api/dotoniwooApi';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

/** Descripción del item con atributos. Preferencia a description sobre product_name */
const getItemDisplayName = (item) => {
  if (!item) return '';
  const base = item.description || item.product_name || item.product?.name || '';
  const attrs = [];
  const w = item.weight_name || item.weight?.weight_name;
  if (w && String(w).toUpperCase() !== 'N/A') attrs.push(w);
  const c = item.color_name || item.color?.color_name;
  if (c && String(c).toUpperCase() !== 'N/A') attrs.push(c);
  const s = item.size_name || item.size?.size_name;
  if (s && String(s).toUpperCase() !== 'N/A') attrs.push(s);
  return attrs.length > 0 ? `${base} ${attrs.join(' ')}` : base;
};

const meses = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const SalesDetailSection = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reportData, setReportData] = useState({
    items: [],
    total_rows: 0,
    total_pages: 1,
    page: 1
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        try {
          const res = await dotoniwoofApi.get('/paymentMethod.php/sales');
          if (res.data?.code === 200) setPaymentMethods(res.data.message || []);
        } catch {
          try {
            const res = await dotoniwoofApi.get('/paymentMethod.php');
            if (res.data?.code === 200) setPaymentMethods(res.data.message || []);
          } catch (e) {
            console.error('Error cargando métodos de pago:', e);
          }
        }
        const catRes = await getCategories();
        if (catRes.ok && catRes.data?.code === 200) setCategories(catRes.data.message || []);
      } catch (error) {
        console.error('Error cargando opciones:', error);
      }
    };
    loadOptions();
  }, []);

  const loadReport = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getSaleReportAccounting(
        selectedYear,
        selectedMonth,
        selectedPaymentMethods,
        selectedCategories,
        page,
        itemsPerPage
      );
      if (res.ok) {
        setReportData({
          items: res.items,
          total_rows: res.total_rows,
          total_pages: res.total_pages,
          page: res.page
        });
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error cargando detalle:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedPaymentMethods, selectedCategories, itemsPerPage]);

  useEffect(() => {
    loadReport(1);
  }, [loadReport]);

  const handleCategoryChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) {
      setSelectedCategories([...selectedCategories, value]);
    } else {
      setSelectedCategories(selectedCategories.filter((id) => id !== value));
    }
    setCurrentPage(1);
  };

  const handleSelectAllCategories = () => {
    const filtered = filteredCategories;
    const ids = filtered.map((c) => c.id_category || c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedCategories.includes(id));
    setSelectedCategories(allSelected ? [] : ids);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedPaymentMethods([]);
    setSelectedCategories([]);
    setCurrentPage(1);
  };

  const filteredCategories = categories.filter((c) => {
    const name = (c.category_name || c.name || '').toLowerCase();
    return name.includes(categorySearch.toLowerCase());
  });

  const hasActiveFilters = selectedPaymentMethods.length > 0 || selectedCategories.length > 0;

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      let allSales = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await getSaleReportAccounting(
          selectedYear,
          selectedMonth,
          selectedPaymentMethods,
          selectedCategories,
          page,
          100
        );
        if (res.ok && res.items?.length > 0) {
          allSales = [...allSales, ...res.items];
          hasMore = page < res.total_pages;
          page++;
        } else {
          hasMore = false;
        }
      }
      if (allSales.length === 0) {
        alert('No hay datos para exportar');
        setLoading(false);
        return;
      }
      const headers = [
        'ID Venta', 'Fecha', 'Método de Pago', 'Usuario', 'Estado', 'Categoría', 'SKU',
        'Descripción', 'Cantidad', 'Precio Unitario', 'Costo Unitario', 'Total Línea', 'Total Venta'
      ];
      const rows = [];
      allSales.forEach((sale) => {
        if (sale.categories?.length > 0) {
          sale.categories.forEach((category) => {
            if (category.items?.length > 0) {
              category.items.forEach((item) => {
                rows.push([
                  sale.id_sale || '', sale.sale_date || '', sale.payment_method?.method_name || '',
                  sale.user_email || '', sale.status || '', category.category_name || '',
                  item.sku || '', item.description || item.product_name || '',
                  (item.quantity || 0).toString(), (item.unit_price || 0).toString(),
                  (item.unit_cost || 0).toString(), (item.line_total || 0).toString(),
                  (sale.total_amount || 0).toString()
                ]);
              });
            } else {
              rows.push([
                sale.id_sale || '', sale.sale_date || '', sale.payment_method?.method_name || '',
                sale.user_email || '', sale.status || '', category.category_name || '',
                '', '', '', '', '', (category.total_amount || 0).toString(),
                (sale.total_amount || 0).toString()
              ]);
            }
          });
        } else {
          rows.push([
            sale.id_sale || '', sale.sale_date || '', sale.payment_method?.method_name || '',
            sale.user_email || '', sale.status || '', '', '', '', '', '', '', '',
            (sale.total_amount || 0).toString()
          ]);
        }
      });
      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `detalle-ventas-${meses[selectedMonth - 1]}-${selectedYear}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exportando CSV:', error);
      alert('Error al exportar el CSV. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sales-detail-section">
      <div className="sales-detail-header">
        <h5 className="sales-detail-title">Detalle de ventas</h5>
        {hasActiveFilters && (
          <button type="button" className="sales-detail-clear-btn" onClick={handleClearFilters}>
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="sales-detail-filters-modern">
        {/* Período */}
        <div className="sales-detail-filter-group sales-detail-period">
          <span className="sales-detail-filter-label">Período</span>
          <div className="sales-detail-period-inputs">
            <Form.Select
              value={selectedYear}
              onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setCurrentPage(1); }}
              className="sales-detail-select-modern"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
            <Form.Select
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(parseInt(e.target.value)); setCurrentPage(1); }}
              className="sales-detail-select-modern"
            >
              {meses.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {/* Métodos de pago - chips */}
        <div className="sales-detail-filter-group">
          <span className="sales-detail-filter-label">Métodos de pago</span>
          <div className="sales-detail-chips">
            {paymentMethods.map((m) => {
              const id = m.id_payment_method || m.id;
              const name = m.method_name || m.payment_method_name || m.name;
              const isActive = selectedPaymentMethods.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`sales-detail-chip ${isActive ? 'sales-detail-chip-active' : ''}`}
                  onClick={() => {
                    if (isActive) {
                      setSelectedPaymentMethods(selectedPaymentMethods.filter((x) => x !== id));
                    } else {
                      setSelectedPaymentMethods([...selectedPaymentMethods, id]);
                    }
                    setCurrentPage(1);
                  }}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Categorías - colapsable con búsqueda */}
        <div className="sales-detail-filter-group sales-detail-categories-group">
          <div className="sales-detail-categories-header">
            <span className="sales-detail-filter-label">
              Categorías
              {selectedCategories.length > 0 && (
                <span className="sales-detail-filter-count"> ({selectedCategories.length})</span>
              )}
            </span>
            <button
              type="button"
              className="sales-detail-expand-btn"
              onClick={() => setCategoriesExpanded(!categoriesExpanded)}
              aria-expanded={categoriesExpanded}
            >
              {categoriesExpanded ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          {categoriesExpanded && (
            <div className="sales-detail-categories-body">
              <div className="sales-detail-categories-toolbar">
                <input
                  type="text"
                  placeholder="Buscar categoría..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="sales-detail-search-input"
                />
                <button
                  type="button"
                  className="sales-detail-select-all-btn"
                  onClick={handleSelectAllCategories}
                >
                  {filteredCategories.length > 0 && filteredCategories.every((c) => selectedCategories.includes(c.id_category || c.id))
                    ? 'Deseleccionar todas'
                    : 'Seleccionar todas'}
                </button>
              </div>
              <div className="sales-detail-categories-list">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((c) => {
                    const id = c.id_category || c.id;
                    const name = c.category_name || c.name;
                    const isChecked = selectedCategories.includes(id);
                    return (
                      <label key={id} className="sales-detail-category-item">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={handleCategoryChange}
                          value={id}
                        />
                        <span className="sales-detail-category-name">{name}</span>
                      </label>
                    );
                  })
                ) : (
                  <span className="sales-detail-no-results">Sin resultados</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Card className="sales-detail-card">
        <Card.Header className="sales-detail-card-header">
          <span>Detalle - {meses[selectedMonth - 1]} {selectedYear}</span>
          <Button
            variant="success"
            size="sm"
            onClick={handleExportCSV}
            disabled={loading}
            className="sales-detail-btn-export"
          >
            {loading ? <><Spinner animation="border" size="sm" className="me-2" />Generando...</> : '📥 Descargar CSV'}
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="sales-detail-loading">
              <Spinner animation="border" />
              <p className="mt-2 mb-0">Cargando ventas...</p>
            </div>
          ) : reportData.items.length > 0 ? (
            <>
              <div className="sales-detail-table-wrap">
                <Table responsive hover className="sales-detail-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Fecha</th>
                      <th>Método Pago</th>
                      <th>Usuario</th>
                      <th>Estado</th>
                      <th>Categoría</th>
                      <th>SKU</th>
                      <th>Descripción</th>
                      <th className="text-end">Cant.</th>
                      <th className="text-end">Precio Unit.</th>
                      <th className="text-end">Costo Unit.</th>
                      <th className="text-end">Total Línea</th>
                      <th className="text-end">Total Venta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.items.map((sale) => {
                      const totalRows = sale.categories?.reduce((s, cat) => s + (cat.items?.length || 1), 0) || 1;
                      const rows = [];
                      let currentRow = 0;
                      sale.categories?.forEach((category, catIndex) => {
                        if (category.items?.length > 0) {
                          category.items.forEach((item, itemIndex) => {
                            const isFirstRow = currentRow === 0;
                            const isFirstInCategory = itemIndex === 0;
                            rows.push(
                              <tr key={`${sale.id_sale}-${category.id_category || catIndex}-${item.id_sale_detail || itemIndex}`}>
                                {isFirstRow && (
                                  <>
                                    <td rowSpan={totalRows}>{sale.id_sale}</td>
                                    <td rowSpan={totalRows}>{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('es-CO') : '-'}</td>
                                    <td rowSpan={totalRows}>{sale.payment_method?.method_name || '-'}</td>
                                    <td rowSpan={totalRows}>{sale.user_email || '-'}</td>
                                    <td rowSpan={totalRows}>
                                      <span className={`sales-detail-status-badge ${(sale.status || '').toLowerCase()}`}>{sale.status || '-'}</span>
                                    </td>
                                  </>
                                )}
                                {isFirstInCategory && <td rowSpan={category.items.length}>{category.category_name}</td>}
                                <td>{item.sku || '-'}</td>
                                <td>{getItemDisplayName(item) || '-'}</td>
                                <td className="text-end">{item.quantity || 0}</td>
                                <td className="text-end">{formatterCOP.format(item.unit_price || 0)}</td>
                                <td className="text-end">{formatterCOP.format(item.unit_cost || 0)}</td>
                                <td className="text-end">{formatterCOP.format(item.line_total || 0)}</td>
                                {isFirstRow && <td rowSpan={totalRows} className="text-end"><strong>{formatterCOP.format(sale.total_amount || 0)}</strong></td>}
                              </tr>
                            );
                            currentRow++;
                          });
                        } else {
                          const isFirstRow = currentRow === 0;
                          rows.push(
                            <tr key={`${sale.id_sale}-${category.id_category || catIndex}`}>
                              {isFirstRow && (
                                <>
                                  <td rowSpan={totalRows}>{sale.id_sale}</td>
                                  <td rowSpan={totalRows}>{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('es-CO') : '-'}</td>
                                  <td rowSpan={totalRows}>{sale.payment_method?.method_name || '-'}</td>
                                  <td rowSpan={totalRows}>{sale.user_email || '-'}</td>
                                  <td rowSpan={totalRows}>
                                    <span className={`sales-detail-status-badge ${(sale.status || '').toLowerCase()}`}>{sale.status || '-'}</span>
                                  </td>
                                </>
                              )}
                              <td>{category.category_name}</td>
                              <td colSpan="5">-</td>
                              <td className="text-end">{formatterCOP.format(category.total_amount || 0)}</td>
                              {isFirstRow && <td rowSpan={totalRows} className="text-end"><strong>{formatterCOP.format(sale.total_amount || 0)}</strong></td>}
                            </tr>
                          );
                          currentRow++;
                        }
                      });
                      if (rows.length === 0) {
                        rows.push(
                          <tr key={sale.id_sale}>
                            <td>{sale.id_sale}</td>
                            <td>{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('es-CO') : '-'}</td>
                            <td>{sale.payment_method?.method_name || '-'}</td>
                            <td>{sale.user_email || '-'}</td>
                            <td><span className={`sales-detail-status-badge ${(sale.status || '').toLowerCase()}`}>{sale.status || '-'}</span></td>
                            <td colSpan="7">-</td>
                            <td className="text-end"><strong>{formatterCOP.format(sale.total_amount || 0)}</strong></td>
                          </tr>
                        );
                      }
                      return rows;
                    })}
                  </tbody>
                </Table>
              </div>
              {reportData.total_pages > 1 && (
                <div className="sales-detail-pagination">
                  <small className="text-muted">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, reportData.total_rows)} de {reportData.total_rows} ventas
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.First disabled={currentPage === 1} onClick={() => loadReport(1)} />
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => loadReport(currentPage - 1)} />
                    {Array.from({ length: reportData.total_pages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === reportData.total_pages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        const showEllipsis = prev && p - prev > 1;
                        return (
                          <React.Fragment key={p}>
                            {showEllipsis && <Pagination.Ellipsis disabled />}
                            <Pagination.Item active={p === currentPage} onClick={() => loadReport(p)}>{p}</Pagination.Item>
                          </React.Fragment>
                        );
                      })}
                    <Pagination.Next disabled={currentPage >= reportData.total_pages} onClick={() => loadReport(currentPage + 1)} />
                    <Pagination.Last disabled={currentPage >= reportData.total_pages} onClick={() => loadReport(reportData.total_pages)} />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <p className="sales-detail-empty">No hay ventas para este período y filtros.</p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SalesDetailSection;
